import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { parseServerError } from "utils/errors";

export const createNewMessage = createAsyncThunk(
  "messages/createNewMessage",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/messages", "POST", data, {
        contentType: "multipart/form-data",
      });
      if (!response.ok) {
        throw new Error("Server error");
      }
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const setMessageRead = createAsyncThunk(
  "messages/setMessageRead",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/messages/set_message_read?message_id=${id}`,
        "PUT"
      );
      if (!response.ok) {
        throw new Error("Server error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteMessage = createAsyncThunk(
  "messages/deleteMessage",
  async ({ id, is_verification_request }, { rejectWithValue }) => {
    try {
      let url = `/messages?message_id=${id}`;
      if (is_verification_request) {
        url += `&is_document_request=true`;
      }
      const response = await callApi(url, "DELETE");
      if (!response.ok) {
        throw new Error("Server error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "messages/fetchMessages",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi("/messages", "GET", params);
      if (!response.ok) {
        throw new Error("Server error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMessageDetail = createAsyncThunk(
  "messages/fetchMessageDetail",
  async (messageId, { rejectWithValue }) => {
    try {
      const response = await callApi("/messages/detail", "GET", {
        message_id: messageId,
      });
      if (!response.ok) {
        throw new Error("Server error");
      }
      const data = await response.json();
      const items =
        Array.isArray(data.items) && data.items.length
          ? data.items
          : data.item != null
            ? [data.item]
            : data.id != null
              ? [data]
              : [];
      return { ...data, items };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const setMessageMarked = createAsyncThunk(
  "messages/setMessageMarked",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/messages/set_marked?message_id=${id}`,
        "PUT"
      );
      if (!response.ok) {
        throw new Error("Server error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  messages: [],
  messagesCount: 0,
  unreadCount: 0,
  markedCount: 0,

  fetchingMessages: false,
  creatingMessage: false,
  deletingMessage: false,

  creatingMessageError: null,
  deletingMessageError: null,
  fetchingMessagesError: null,
  setMessageMarkedError: null,
  setMessageReadError: null,
};

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.fetchingMessages = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.fetchingMessages = false;
        state.fetchingMessagesError = null;
        state.messages = action.payload.items;
        state.messagesCount = action.payload.count;
        state.unreadCount = action.payload.unread_count || 0;
        state.markedCount = action.payload.marked_count || 0;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.fetchingMessages = false;
        state.fetchingMessagesError = parseServerError(action.error);
      })
      .addCase(fetchMessageDetail.pending, (state) => {
        state.fetchingMessages = true;
      })
      .addCase(fetchMessageDetail.fulfilled, (state, action) => {
        state.fetchingMessages = false;
        state.fetchingMessagesError = null;
        state.messages = action.payload.items || [];
        if (typeof action.payload.count === "number") {
          state.messagesCount = action.payload.count;
        }
      })
      .addCase(fetchMessageDetail.rejected, (state, action) => {
        state.fetchingMessages = false;
        state.fetchingMessagesError = parseServerError(action.error);
      })
      .addCase(createNewMessage.pending, (state) => {
        state.creatingMessage = true;
      })
      .addCase(createNewMessage.fulfilled, (state) => {
        state.creatingMessage = false;
        state.creatingMessageError = null;
      })
      .addCase(createNewMessage.rejected, (state, action) => {
        state.creatingMessage = false;
        state.creatingMessageError = parseServerError(action.error);
      })
      .addCase(deleteMessage.pending, (state) => {
        state.deletingMessage = true;
      })
      .addCase(deleteMessage.fulfilled, (state) => {
        state.deletingMessage = false;
        state.deletingMessageError = null;
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.deletingMessage = false;
        state.deletingMessageError = parseServerError(action.error);
      })
      .addCase(setMessageMarked.fulfilled, (state, action) => {
        state.setMessageMarkedError = null;
        const { message_id, marked } = action.payload;
        const messageIndex = state.messages.findIndex(
          (message) => message.id === message_id
        );
        if (messageIndex !== -1) {
          const previousMarked = state.messages[messageIndex].marked;
          state.messages[messageIndex].marked = marked;
          
          // Update markedCount
          if (marked && !previousMarked) {
            state.markedCount = (state.markedCount || 0) + 1;
          } else if (!marked && previousMarked) {
            state.markedCount = Math.max(0, (state.markedCount || 0) - 1);
          }
        }
      })
      .addCase(setMessageMarked.rejected, (state, action) => {
        state.setMessageMarkedError = parseServerError(action.error);
      })
      .addCase(setMessageRead.fulfilled, (state, action) => {
        state.setMessageReadError = null;
        const messageId = action.meta.arg;
        const messageIndex = state.messages.findIndex(
          (message) => String(message.id) === String(messageId)
        );
        if (messageIndex !== -1 && !state.messages[messageIndex].read) {
          state.messages[messageIndex].read = true;
          state.unreadCount = Math.max(0, (state.unreadCount || 0) - 1);
        }
      })
      .addCase(setMessageRead.rejected, (state, action) => {
        state.setMessageReadError = parseServerError(action.error);
      }),
});

export default messagesSlice.reducer;
